import React, { useState } from 'react'
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link as MuiLink,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import StarIcon from '@mui/icons-material/Star'
import WhatshotIcon from '@mui/icons-material/Whatshot'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import { GameINFOV2, PartnerPageData } from 'types/types'
import { isBattleRoyaleGame, isGameStarted } from 'types/utils'
import { isStarSportsPage } from '@/utils/starSportsUtils'
import Link from 'next/link'
import GameCountdown from './GameCountdown'

const battleAccent = '#DC2626'
const POINT_LABEL_EMOJIS: [string, string][] = [
  ['red card', '🟥'],
  ['yellow card', '🟨'],
  ['shot on target', '🎯'],
  ['goal', '🥅'],
  ['assist', '🤝'],
  ['shot', '⚽'],
  ['foul', '🚫'],
  ['tackle', '🤼'],
]

function getPointEmoji(label: string): string {
  const lowerLabel = label.toLowerCase()
  const match = POINT_LABEL_EMOJIS.find(([keyword]) => lowerLabel.includes(keyword))
  return match ? match[1] : ''
}

interface GameInfoCardProps {
  page: PartnerPageData
  game: GameINFOV2 | null
  gameRulesLink?: string
  onToggleGameInfo?: (open: boolean) => void
  isInProgress?: boolean
  hasMultipleRounds?: boolean
  nextRoundStartTime?: string | null
}

function GameInfoCard({
  page,
  game,
  gameRulesLink,
  onToggleGameInfo,
  isInProgress,
  hasMultipleRounds,
  nextRoundStartTime,
}: GameInfoCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const primaryColor = '#FFC107'
  const secondaryColor = '#FF9800'
  const textColorDark = '#353535'
  const borderColor = '#F1D88A'
  const prizeBackground = '#FFF3CD'
  const textColorLight = '#787878e6'
  // use prop if provided, otherwise check if game is ACTIVE and has started
  const hasUpcomingRound = hasMultipleRounds && !!nextRoundStartTime
  const gameIsInProgress = hasUpcomingRound
    ? false
    : isInProgress ?? (game?.status === 'ACTIVE' && isGameStarted(game))

  const defaultGameRulesLink = `/${page.company_domain}/gamerules${
    game?.id ? `?gameId=${game.id}` : ''
  }`

  // Get dynamic data from game object
  const howToPlay = game?.how_to_play
  const prizeHeader = page?.prize_header
  const prizes = game?.prizes
  const pointsSystem = game?.points_system
  const jackpot = game?.jackpot

  const hasHowToPlay = howToPlay?.title && howToPlay?.description
  const hasStructuredPrizes = prizes?.prizes && prizes.prizes.length > 0
  const hasPrizes = hasStructuredPrizes || !!prizeHeader
  const hasPointsSystem = pointsSystem?.points && pointsSystem.points.length > 0
  const hasJackpot = jackpot?.amount && jackpot?.requirements

  const showBattleRoyalePoints = isBattleRoyaleGame(game) && hasPointsSystem

  const gameInfoContent = (
    <Box sx={isMobile ? { px: 2, pb: 3 } : {}}>
      {/* HOW TO PLAY - only show if data exists */}
      {hasHowToPlay && (
        <Box sx={{ mb: 3, mt: 1 }}>
          <Typography sx={{ fontSize: '0.875rem', color: textColorLight, mb: 1 }}>
            {howToPlay.description}
          </Typography>
          <MuiLink
            component={Link}
            href={gameRulesLink || defaultGameRulesLink}
            sx={{
              fontSize: '0.8125rem',
              color: secondaryColor,
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Detailed game rules
          </MuiLink>
        </Box>
      )}

      {/* POINTS SYSTEM - only show if data exists. Battle Royale games show
          this up front (see showBattleRoyalePoints above) instead, so skip
          the duplicate here. */}
      {hasPointsSystem && !showBattleRoyalePoints && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 700,
              color: textColorDark,
              mb: 1.5,
              fontStyle: 'italic',
              textTransform: 'uppercase',
            }}
          >
            {pointsSystem.title}
          </Typography>

          <Box
            sx={{
              backgroundColor: '#FAFAF8',
              borderRadius: '12px',
              px: 2,
              py: 1,
            }}
          >
            {pointsSystem.points.map((item, index) => (
              <Box key={index}>
                {index > 0 && <Divider sx={{ borderTop: `1px solid #e0e0e0`, my: 1 }} />}
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                  }}
                >
                  <Typography sx={{ fontSize: '0.875rem', color: textColorDark }}>
                    {item.label}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: prizeBackground,
                      color: secondaryColor,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '999px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.value}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Learn more link */}
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: '0.8125rem', color: textColorDark }}>
          Learn more about{' '}
          <MuiLink
            component={Link}
            href={`/${page.company_domain}/landing`}
            sx={{
              fontSize: '0.8125rem',
              color: secondaryColor,
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {page.company_name || page.company_domain}
          </MuiLink>
        </Typography>
      </Box>
    </Box>
  )

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      {/* Game starts in row */}
      <Box sx={{ p: 2 }}>
        <GameCountdown
          startTime={
            hasMultipleRounds && nextRoundStartTime ? nextRoundStartTime : game?.start_time
          }
          isInProgress={gameIsInProgress}
          status={game?.status}
          iconSize={20}
          fontSize={16}
          label={
            hasMultipleRounds && nextRoundStartTime && game?.status !== 'FINISHED'
              ? 'Round starts in'
              : undefined
          }
          showLivePill={true}
        />
      </Box>

      {/* Prize pool and Jackpot section */}
      <Box sx={{ px: 2, py: 1.5 }}>
        {(hasPrizes || hasJackpot) && (
          <Box sx={{ mb: 0 }}>
            <Box
              sx={{
                backgroundColor: prizeBackground,
                borderRadius: '0.75rem',
                px: 2,
                py: 1,
                border: `1px solid ${borderColor}`,
              }}
            >
              {/* Jackpot - only shown if jackpot data exists */}
              {hasJackpot && (
                <Box sx={{ pt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <StarIcon sx={{ fontSize: '1.5625rem', color: primaryColor }} />
                    <Typography
                      sx={{
                        fontSize: '1.15rem',
                        fontWeight: 700,
                        color: textColorDark,
                      }}
                    >
                      Jackpot
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 800,
                        color: textColorDark,
                        ml: 'auto',
                      }}
                    >
                      {jackpot.amount}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', color: textColorLight }}>
                    {jackpot.requirements}
                  </Typography>
                </Box>
              )}

              {/* Prizes - shown regardless of whether jackpot exists */}
              {hasPrizes && (
                <Box sx={{ pt: hasJackpot ? 0 : 1 }}>
                  {hasJackpot && <Divider sx={{ borderTop: `1px solid ${borderColor}`, my: 1 }} />}
                  {hasStructuredPrizes ? (
                    prizes.prizes.map((prize, index) => (
                      <Box key={index}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: '0.875rem', color: textColorDark, fontWeight: 500 }}
                          >
                            {prize.label}
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.875rem', color: textColorDark, fontWeight: 800 }}
                          >
                            {prize.value}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.5,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: '0.875rem', color: textColorDark, fontWeight: 500 }}
                      >
                        Prize pool
                      </Typography>
                      <Typography
                        sx={{ fontSize: '0.875rem', color: textColorDark, fontWeight: 800 }}
                      >
                        {prizeHeader}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Players entered - part of the same prize card, separated by a divider */}
              {page.feature_flags.show_players_entered !== false &&
                game?.participant_count != null && (
                  <Box sx={{ pt: 0 }}>
                    <Divider sx={{ borderTop: `1px solid ${borderColor}`, my: 1 }} />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <GroupOutlinedIcon sx={{ fontSize: '1.15rem', color: primaryColor }} />
                        <Typography
                          sx={{ fontSize: '0.875rem', color: textColorDark, fontWeight: 700 }}
                        >
                          Players entered
                        </Typography>
                      </Box>
                      <Typography
                        sx={{ fontSize: '0.875rem', color: textColorDark, fontWeight: 800 }}
                      >
                        {game.participant_count.toLocaleString('en-GB')}
                      </Typography>
                    </Box>
                  </Box>
                )}
            </Box>
          </Box>
        )}
        {/* Battle Royale scoring - all admin-entered points in one red section */}
        {showBattleRoyalePoints && (
          <Box
            sx={{
              background: `linear-gradient(135deg, ${battleAccent}17, ${battleAccent}06)`,
              border: `1px solid ${battleAccent}30`,
              borderRadius: '0.75rem',
              px: 2,
              py: 1,
              mt: hasPrizes || hasJackpot ? 1.5 : 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: battleAccent,
                  color: 'white',
                }}
              >
                <WhatshotIcon sx={{ fontSize: '1.1rem' }} />
              </Box>
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 800,
                  color: textColorDark,
                }}
              >
                Battle Royale Points
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                pb: 0.25,
              }}
            >
              {pointsSystem?.points.map((point, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.6,
                    bgcolor: 'white',
                    border: `1px solid ${battleAccent}40`,
                    borderRadius: '999px',
                    pl: 0.5,
                    pr: 1.25,
                    py: 0.4,
                    maxWidth: '100%',
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: battleAccent,
                      color: 'white',
                      fontWeight: 800,
                      borderRadius: '999px',
                      px: 1,
                      py: 0.35,
                      lineHeight: 1.4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      minWidth: 0,
                    }}
                  >
                    {getPointEmoji(point.label) && (
                      <Box
                        component="span"
                        sx={{ fontSize: '0.9rem', lineHeight: 1, flexShrink: 0 }}
                      >
                        {getPointEmoji(point.label)}
                      </Box>
                    )}
                    <Box component="span" sx={{ fontSize: '0.75rem', wordBreak: 'break-word' }}>
                      {point.label}
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: textColorDark,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {point.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Detailed prize information - always shown, even with just a jackpot
          and no prize/prizeHeader data */}
      <Box px={2} pt={1}>
        <MuiLink
          component={Link}
          href={`/${page.company_domain}/prize${game?.id ? `?gameId=${game.id}` : ''}`}
          sx={{
            fontSize: '0.875rem',
            color: secondaryColor,
            textDecoration: 'underline',
            cursor: 'pointer',
            mt: 1,
            display: 'inline-block',
          }}
        >
          Detailed prize information
        </MuiLink>
      </Box>

      {/* Mobile: Click to open bottom sheet */}
      {isMobile ? (
        <>
          <Box
            onClick={() => {
              setDrawerOpen(true)
              onToggleGameInfo?.(true)
            }}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: '1px solid #E0E0E0',
              p: 2,
              mt: 1.5,
              cursor: 'pointer',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: textColorDark,
                mb: 0,
                fontStyle: 'italic',
                textTransform: 'uppercase',
              }}
            >
              How to play
            </Typography>
            <ChevronRightIcon sx={{ color: textColorDark }} />
          </Box>

          {/* Bottom Sheet Drawer */}
          <Drawer
            anchor="bottom"
            open={drawerOpen}
            onClose={() => {
              setDrawerOpen(false)
              onToggleGameInfo?.(false)
            }}
            PaperProps={{
              sx: {
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
                maxHeight: '85vh',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                p: 1,
                pb: 0,
              }}
            >
              <IconButton
                onClick={() => {
                  setDrawerOpen(false)
                  onToggleGameInfo?.(false)
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ overflowY: 'auto' }}>{gameInfoContent}</Box>
          </Drawer>
        </>
      ) : (
        /* Desktop: Expandable accordion */
        <Accordion
          expanded={expanded}
          onChange={() => {
            const newExpanded = !expanded
            setExpanded(newExpanded)
            onToggleGameInfo?.(newExpanded)
          }}
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '& .MuiAccordionSummary-root': {
              borderTop: '1px solid #E0E0E0',
              minHeight: '48px',
              mt: 1.5,
            },
            '& .MuiAccordionSummary-content': {
              margin: '12px 0',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              '& .MuiAccordionSummary-expandIconWrapper': {
                color: textColorDark,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: textColorDark,
                mb: 0,
                fontStyle: 'italic',
                textTransform: 'uppercase',
              }}
            >
              How to play
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 3, px: 2 }}>{gameInfoContent}</AccordionDetails>
        </Accordion>
      )}
    </Box>
  )
}

export default GameInfoCard
